/**
 * COI Theme Configuration
 *
 * Consolidated theme module that:
 * - Imports seamless theme from external library
 * - Defines COI-specific color constants
 * - Provides theme tokens for JS/TS usage
 * - Provides CSS variables for SCSS usage
 * - Extends seamless theme with custom component overrides
 *
 * Usage Examples:
 *   // In TypeScript/TSX:
 *   import { ThemeTokens, COI_COLORS } from '@/utilities/theme';
 *   <Box sx={{ backgroundColor: ThemeTokens.colors.primary.main }}>
 *
 *   // In SCSS:
 *   .myClass {
 *     color: var(--color-primary-main);
 *     background: var(--color-status-approved);
 *   }
 */

import { claTheme, claDarkTheme, claColorPalette } from 'lib-seamlesscomponents-react';
import { createTheme, Theme } from '@mui/material/styles';
import { StatusOptions } from '../constants/constants';

// ============================================================================
// SEAMLESS THEME WRAPPER
// ============================================================================

/**
 * Access seamless theme as a typed Theme object
 * Note: Using 'as any as Theme' to handle MUI version differences between seamless lib and main project
 */
export const seamlessTheme: Theme = claTheme as any as Theme;

// ============================================================================
// COI COLOR CONSTANTS
// ============================================================================

/**
 * COI-specific color constants
 * Pulls from seamless color palette with fallbacks for robustness
 */
export const COI_COLORS = {
  // Status Colors (from claColorPalette)
  APPROVED: claColorPalette['approved'],
  REJECTED: claColorPalette['declined'],
  DECLINED: claColorPalette['declined'],
  SUBMITTED: claColorPalette['orange'],
  REVIEW_NEEDED: claColorPalette['navy'],
  CONSULT_REQUESTED: claColorPalette['navy'],
  DRAFT: claColorPalette['navy'],

  // Primary Colors
  PRIMARY: seamlessTheme.palette.primary.main,
  PRIMARY_DARK: seamlessTheme.palette.primary.dark,
  PRIMARY_LIGHT: seamlessTheme.palette.primary.light,

  // Secondary Colors
  SECONDARY: seamlessTheme.palette.secondary.main,
  SECONDARY_DARK: seamlessTheme.palette.secondary.dark,
  SECONDARY_LIGHT: seamlessTheme.palette.secondary.light,

  // Semantic Colors
  SUCCESS: seamlessTheme.palette.success.main,
  ERROR: seamlessTheme.palette.error.main,
  WARNING: seamlessTheme.palette.warning.main,
  INFO: seamlessTheme.palette.info.main,

  // UI Colors
  CYAN_TEAL: claColorPalette['riptide-400'],
  NAVY_PRIMARY: claColorPalette['navy'],
  NAVY_BORDER: claColorPalette['navy-400'],

  // Neutral Colors
  GREY_LIGHT: claColorPalette['lightGrey'] || seamlessTheme.palette.grey[100],
  GREY_MEDIUM: claColorPalette['mediumGrey'] || seamlessTheme.palette.grey[400],
  GREY_DARK: claColorPalette['darkGrey'] || seamlessTheme.palette.grey[700],

  // Text Colors
  TEXT_PRIMARY: seamlessTheme.palette.text.primary,
  TEXT_SECONDARY: seamlessTheme.palette.text.secondary,
  TEXT_DISABLED: seamlessTheme.palette.text.disabled,

  // Background Colors
  BACKGROUND_DEFAULT: seamlessTheme.palette.background.default,
  BACKGROUND_PAPER: seamlessTheme.palette.background.paper,
} as const;

/**
 * Get color for a given status
 * Returns CSS variable reference for runtime theme switching
 * @param status - The status value
 * @returns CSS variable reference for the status color
 */
export const getStatusColor = (status: string): string => {
  switch (status) {
    case StatusOptions.Approved:
      return 'var(--color-status-approved)';
    case StatusOptions.Rejected:
      return 'var(--color-status-rejected)';
    case StatusOptions.Submitted:
      return 'var(--color-status-submitted)';
    case StatusOptions.ReviewNeeded:
    case StatusOptions.ConsultRequested:
    case StatusOptions.Draft:
      return 'var(--color-status-draft)';
    default:
      return 'var(--color-primary-main)';
  }
};

// ============================================================================
// THEME TOKENS
// ============================================================================

/**
 * Theme tokens for programmatic access
 * Use these in TypeScript/TSX code for type-safe theme access
 */
export const ThemeTokens = {
  // Palette colors
  colors: {
    primary: {
      main: seamlessTheme.palette.primary.main,
      dark: seamlessTheme.palette.primary.dark,
      light: seamlessTheme.palette.primary.light,
      contrastText: seamlessTheme.palette.primary.contrastText,
    },
    secondary: {
      main: seamlessTheme.palette.secondary.main,
      dark: seamlessTheme.palette.secondary.dark,
      light: seamlessTheme.palette.secondary.light,
      contrastText: seamlessTheme.palette.secondary.contrastText,
    },
    error: {
      main: seamlessTheme.palette.error.main,
      dark: seamlessTheme.palette.error.dark,
      light: seamlessTheme.palette.error.light,
      contrastText: seamlessTheme.palette.error.contrastText,
    },
    warning: {
      main: seamlessTheme.palette.warning.main,
      dark: seamlessTheme.palette.warning.dark,
      light: seamlessTheme.palette.warning.light,
    },
    info: {
      main: seamlessTheme.palette.info.main,
      dark: seamlessTheme.palette.info.dark,
      light: seamlessTheme.palette.info.light,
    },
    success: {
      main: seamlessTheme.palette.success.main,
      dark: seamlessTheme.palette.success.dark,
      light: seamlessTheme.palette.success.light,
    },
    text: {
      primary: seamlessTheme.palette.text.primary,
      secondary: seamlessTheme.palette.text.secondary,
      disabled: seamlessTheme.palette.text.disabled,
    },
    background: {
      default: seamlessTheme.palette.background.default,
      paper: seamlessTheme.palette.background.paper,
    },
    action: {
      active: seamlessTheme.palette.action.active,
      hover: seamlessTheme.palette.action.hover,
      selected: seamlessTheme.palette.action.selected,
      disabled: seamlessTheme.palette.action.disabled,
      disabledBackground: seamlessTheme.palette.action.disabledBackground,
    },
    divider: seamlessTheme.palette.divider,
    grey: seamlessTheme.palette.grey,
  },

  // Spacing tokens
  spacing: seamlessTheme.spacing,

  // Shape tokens
  shape: {
    borderRadius: seamlessTheme.shape.borderRadius,
  },

  // Typography tokens
  typography: {
    fontFamily: seamlessTheme.typography.fontFamily,
    fontSize: seamlessTheme.typography.fontSize,
    fontWeightLight: seamlessTheme.typography.fontWeightLight,
    fontWeightRegular: seamlessTheme.typography.fontWeightRegular,
    fontWeightMedium: seamlessTheme.typography.fontWeightMedium,
    fontWeightBold: seamlessTheme.typography.fontWeightBold,
  },

  // Shadow tokens
  shadows: seamlessTheme.shadows,

  // Z-index tokens
  zIndex: seamlessTheme.zIndex,

  // Transitions
  transitions: seamlessTheme.transitions,
} as const;

// ============================================================================
// CSS VARIABLES
// ============================================================================

/**
 * CSS Custom Properties for use in SCSS/CSS modules
 * Injected into document root via injectThemeVariables()
 *
 * Usage in SCSS:
 *   .myClass {
 *     color: var(--color-primary-main);
 *     background: var(--color-background-paper);
 *     border-color: var(--color-status-approved);
 *   }
 */
export const cssVariables = {
  // Primary colors
  '--color-primary-main': ThemeTokens.colors.primary.main,
  '--color-primary-dark': ThemeTokens.colors.primary.dark,
  '--color-primary-light': ThemeTokens.colors.primary.light,
  '--color-primary-contrast': ThemeTokens.colors.primary.contrastText,

  // Secondary colors
  '--color-secondary-main': ThemeTokens.colors.secondary.main,
  '--color-secondary-dark': ThemeTokens.colors.secondary.dark,
  '--color-secondary-light': ThemeTokens.colors.secondary.light,
  '--color-secondary-contrast': ThemeTokens.colors.secondary.contrastText,

  // Semantic colors
  '--color-error-main': ThemeTokens.colors.error.main,
  '--color-error-dark': ThemeTokens.colors.error.dark,
  '--color-error-light': ThemeTokens.colors.error.light,
  '--color-success-main': ThemeTokens.colors.success.main,
  '--color-success-dark': ThemeTokens.colors.success.dark,
  '--color-success-light': ThemeTokens.colors.success.light,
  '--color-warning-main': ThemeTokens.colors.warning.main,
  '--color-warning-dark': ThemeTokens.colors.warning.dark,
  '--color-warning-light': ThemeTokens.colors.warning.light,
  '--color-info-main': ThemeTokens.colors.info.main,
  '--color-info-dark': ThemeTokens.colors.info.dark,
  '--color-info-light': ThemeTokens.colors.info.light,

  // Status colors (COI-specific)
  '--color-status-approved': COI_COLORS.APPROVED,
  '--color-status-rejected': COI_COLORS.REJECTED,
  '--color-status-submitted': COI_COLORS.SUBMITTED,
  '--color-status-draft': COI_COLORS.DRAFT,

  // Text colors
  '--color-text-primary': ThemeTokens.colors.text.primary,
  '--color-text-secondary': ThemeTokens.colors.text.secondary,
  '--color-text-disabled': ThemeTokens.colors.text.disabled,

  // Background colors
  '--color-background-default': ThemeTokens.colors.background.default,
  '--color-background-paper': ThemeTokens.colors.background.paper,

  // Action colors
  '--color-action-active': ThemeTokens.colors.action.active,
  '--color-action-hover': ThemeTokens.colors.action.hover,
  '--color-action-selected': ThemeTokens.colors.action.selected,
  '--color-action-disabled': ThemeTokens.colors.action.disabled,
  '--color-action-disabled-background': ThemeTokens.colors.action.disabledBackground,

  // Grey scale
  '--color-grey-50': ThemeTokens.colors.grey[50],
  '--color-grey-100': ThemeTokens.colors.grey[100],
  '--color-grey-200': ThemeTokens.colors.grey[200],
  '--color-grey-300': ThemeTokens.colors.grey[300],
  '--color-grey-400': ThemeTokens.colors.grey[400],
  '--color-grey-500': ThemeTokens.colors.grey[500],
  '--color-grey-600': ThemeTokens.colors.grey[600],
  '--color-grey-700': ThemeTokens.colors.grey[700],
  '--color-grey-800': ThemeTokens.colors.grey[800],
  '--color-grey-900': ThemeTokens.colors.grey[900],

  // Divider
  '--color-divider': ThemeTokens.colors.divider,

  // COI-specific colors
  '--color-navy-border': COI_COLORS.NAVY_BORDER,
  '--color-cyan-teal': COI_COLORS.CYAN_TEAL,

  // Border radius
  '--shape-border-radius': `${ThemeTokens.shape.borderRadius}px`,

  // Spacing (multiples of base spacing unit)
  '--spacing-1': ThemeTokens.spacing(1),
  '--spacing-2': ThemeTokens.spacing(2),
  '--spacing-3': ThemeTokens.spacing(3),
  '--spacing-4': ThemeTokens.spacing(4),
  '--spacing-5': ThemeTokens.spacing(5),
  '--spacing-6': ThemeTokens.spacing(6),
  '--spacing-8': ThemeTokens.spacing(8),
} as const;

/**
 * Inject CSS custom properties into the document root
 * Call this once in App.tsx on mount
 */
export function injectThemeVariables(): void {
  const root = document.documentElement;
  Object.entries(cssVariables).forEach(([key, value]) => {
    root.style.setProperty(key, String(value));
  });
}

// ============================================================================
// EXTENDED THEMES
// ============================================================================

/**
 * Extended theme with COI-specific component overrides
 * Maintains seamless theme as base while adding custom styling
 */
export const coiTheme: Theme = createTheme(claTheme as any, {
  components: {
    // TextField and Input component styling
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: COI_COLORS.CYAN_TEAL, // Teal/cyan hover state
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: COI_COLORS.CYAN_TEAL, // Teal/cyan focus state
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: COI_COLORS.CYAN_TEAL, // Teal/cyan hover state
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: COI_COLORS.CYAN_TEAL, // Teal/cyan focus state
            borderWidth: '2px',
          },
        },
      },
    },
    // Button styling
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', // PascalCase instead of UPPERCASE
          fontWeight: 600,
        },
        contained: {
          'boxShadow': 'none',
          '&.MuiButton-colorPrimary.Mui-disabled': {
            backgroundColor: seamlessTheme.palette.action.disabledBackground,
            color: seamlessTheme.palette.primary.contrastText,
          },
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    // Checkbox styling
    MuiCheckbox: {
      styleOverrides: {
        root: {
          'color': COI_COLORS.PRIMARY,
          '&.Mui-checked': {
            color: COI_COLORS.PRIMARY,
          },
        },
      },
    },
    // Autocomplete styling
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: COI_COLORS.SECONDARY,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: COI_COLORS.SECONDARY,
              borderWidth: '2px',
            },
          },
        },
      },
    },
    // Select styling
    MuiSelect: {
      styleOverrides: {
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: COI_COLORS.SECONDARY,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: COI_COLORS.SECONDARY,
            borderWidth: '2px',
          },
        },
      },
    },
  },
});

/**
 * Extended dark theme with COI-specific overrides
 */
export const coiDarkTheme: Theme = createTheme(claDarkTheme as any, {
  components: {
    // Apply same overrides for dark theme
    ...coiTheme.components,
  },
});

// ============================================================================
// EXPORTS
// ============================================================================

export default ThemeTokens;
